/**
 * CoachInvitations — coach page for sending and viewing their own invitations.
 *
 * Two sections:
 * 1. "Send Invitation" collapsible form card.
 * 2. Summary card with breakdown by intended_role.
 * 3. Invitations table (email, role, status, expires, created) — no revoke action.
 *
 * Route: /coach/invitations (protected by AuthGuard in App.tsx)
 */
import { useState, useEffect, useCallback } from 'react';
import {
  EnvelopeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/Button';
import Input from '@/components/ui/Input';
import { createCoachInvitation, listCoachInvitations } from '@/services/adminApi';
import type { RegistrationInvitation, InvitationStatus } from '@/types/admin';
import { formatDate } from '@/utils/formatters';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveStatus(inv: RegistrationInvitation): InvitationStatus {
  if (inv.used_at) return 'used';
  if (new Date(inv.expires_at) <= new Date()) return 'expired';
  return 'active';
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: InvitationStatus }) {
  const config: Record<InvitationStatus, { label: string; color: string; bg: string }> = {
    active: {
      label: 'Active',
      color: 'var(--color-accent)',
      bg: 'rgba(59,130,246,0.12)',
    },
    used: {
      label: 'Used',
      color: 'var(--color-accent-green, #34D399)',
      bg: 'rgba(52,211,153,0.12)',
    },
    expired: {
      label: 'Expired',
      color: 'var(--color-accent-gold)',
      bg: 'rgba(251,191,36,0.12)',
    },
  };
  const c = config[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 500,
        color: c.color,
        backgroundColor: c.bg,
      }}
    >
      {c.label}
    </span>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}

function Toast({ message, type, onDismiss }: ToastProps) {
  const isError = type === 'error';
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: isError ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)',
        border: `1px solid ${isError ? 'rgba(248,113,113,0.35)' : 'rgba(52,211,153,0.35)'}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        maxWidth: '420px',
        color: 'var(--color-text-primary)',
        fontSize: '14px',
      }}
    >
      {isError ? (
        <ExclamationCircleIcon style={{ width: '18px', height: '18px', flexShrink: 0, color: '#F87171', marginTop: '1px' }} />
      ) : (
        <CheckCircleIcon style={{ width: '18px', height: '18px', flexShrink: 0, color: '#34D399', marginTop: '1px' }} />
      )}
      <span style={{ flex: 1, lineHeight: '1.5' }}>{message}</span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onDismiss}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', marginTop: '1px' }}
      >
        <XMarkIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
}

// ─── Send Invitation Form ─────────────────────────────────────────────────────

interface SendFormProps {
  onCreated: (inv: RegistrationInvitation) => void;
}

function SendInvitationForm({ onCreated }: SendFormProps) {
  const [open, setOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'coach' | 'athlete'>('athlete');
  const [coachTier, setCoachTier] = useState<'solo' | 'team' | 'enterprise'>('solo');
  const [expiresInDays, setExpiresInDays] = useState('7');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const inv = await createCoachInvitation({
        email: email.trim(),
        intended_role: role,
        intended_coach_tier: role === 'coach' ? coachTier : null,
        expires_in_days: parseInt(expiresInDays, 10) || 7,
      });
      onCreated(inv);
      setEmail('');
      setRole('athlete');
      setCoachTier('solo');
      setExpiresInDays('7');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to send invitation. Please try again.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-2)',
    color: 'var(--color-text-primary)',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-elevated-2)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '14px',
        overflow: 'hidden',
        marginBottom: '24px',
      }}
    >
      {/* Card header — toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-primary)',
        }}
      >
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '16px' }}>
          Send Invitation
        </span>
        {open ? (
          <ChevronUpIcon style={{ width: '18px', height: '18px', color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronDownIcon style={{ width: '18px', height: '18px', color: 'var(--color-text-muted)' }} />
        )}
      </button>

      {open && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)', marginBottom: '20px' }} />

          {error && (
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                backgroundColor: 'rgba(248,113,113,0.1)',
                border: '1px solid rgba(248,113,113,0.25)',
                color: '#F87171',
                fontSize: '13px',
                marginBottom: '16px',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {/* Email */}
              <div style={{ gridColumn: '1 / -1' }}>
                <Input
                  label="Email address *"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="athlete@example.com"
                  autoComplete="off"
                />
              </div>

              {/* Member type */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                  Member type *
                </label>
                <select value={role} onChange={(e) => setRole(e.target.value as 'coach' | 'athlete')} style={selectStyle}>
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                </select>
              </div>

              {/* Coach tier (only when role=coach) */}
              {role === 'coach' && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                    Coach tier
                  </label>
                  <select
                    value={coachTier}
                    onChange={(e) => setCoachTier(e.target.value as 'solo' | 'team' | 'enterprise')}
                    style={selectStyle}
                  >
                    <option value="solo">Solo</option>
                    <option value="team">Team</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              )}

              {/* Expires in days */}
              <div>
                <Input
                  label="Expires in (days)"
                  type="number"
                  min={1}
                  max={90}
                  required
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                />
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send invitation'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function SummaryCard({ invitations }: { invitations: RegistrationInvitation[] }) {
  const athletes = invitations.filter((i) => i.intended_role === 'athlete').length;
  const coaches = invitations.filter((i) => i.intended_role === 'coach').length;
  const active = invitations.filter((i) => deriveStatus(i) === 'active').length;
  const used = invitations.filter((i) => deriveStatus(i) === 'used').length;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '12px',
        marginBottom: '24px',
      }}
    >
      {[
        { label: 'Athletes invited', value: athletes, color: 'var(--color-text-secondary)' },
        { label: 'Coaches invited', value: coaches, color: 'var(--color-accent-blue, #60A5FA)' },
        { label: 'Active / pending', value: active, color: 'var(--color-accent)' },
        { label: 'Redeemed', value: used, color: 'var(--color-accent-green, #34D399)' },
      ].map((stat) => (
        <div
          key={stat.label}
          style={{
            backgroundColor: 'var(--color-dark-elevated-2)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '12px',
            padding: '16px 20px',
          }}
        >
          <div style={{ fontSize: '24px', fontWeight: 700, color: stat.color, fontFamily: 'var(--font-display)' }}>
            {stat.value}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Filter chips ─────────────────────────────────────────────────────────────

type FilterValue = 'all' | InvitationStatus;

function FilterChips({ value, onChange }: { value: FilterValue; onChange: (v: FilterValue) => void }) {
  const chips: { value: FilterValue; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'used', label: 'Used' },
    { value: 'expired', label: 'Expired' },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {chips.map((chip) => {
        const isActive = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            style={{
              padding: '5px 14px',
              borderRadius: '20px',
              border: `1px solid ${isActive ? 'var(--color-accent-blue, #60A5FA)' : 'var(--color-border-subtle)'}`,
              backgroundColor: isActive ? 'rgba(96,165,250,0.12)' : 'transparent',
              color: isActive ? 'var(--color-accent-blue, #60A5FA)' : 'var(--color-text-secondary)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 120ms ease',
            }}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function CoachInvitations() {
  const [invitations, setInvitations] = useState<RegistrationInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterValue>('all');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCoachInvitations(filter !== 'all' ? { status: filter } : {});
      setInvitations(data.items);
    } catch {
      showToast('Failed to load invitations.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  // Load all invitations for summary counts (always unfiltered)
  const [allInvitations, setAllInvitations] = useState<RegistrationInvitation[]>([]);
  useEffect(() => {
    listCoachInvitations({})
      .then((d) => setAllInvitations(d.items))
      .catch(() => null);
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleCreated = useCallback(
    (inv: RegistrationInvitation) => {
      showToast(`Invitation sent to ${inv.email}.`);
      setAllInvitations((prev) => [inv, ...prev]);
      fetchInvitations();
    },
    [fetchInvitations, showToast]
  );

  const cellStyle: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    borderBottom: '1px solid var(--color-border-subtle)',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '10px 16px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border-subtle)',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' }}>
      {/* Page header */}
      <div style={{ marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <EnvelopeIcon style={{ width: '28px', height: '28px', color: 'var(--color-accent-blue, #60A5FA)', flexShrink: 0 }} />
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: '28px',
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            Invitations
          </h1>
          <p style={{ marginTop: '4px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
            Invite athletes and coaches to join your team
          </p>
        </div>
      </div>

      {/* Summary card */}
      <SummaryCard invitations={allInvitations} />

      {/* Send invitation form */}
      <SendInvitationForm onCreated={handleCreated} />

      {/* Invitations table */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-elevated-2)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        {/* Table header row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: '16px',
              color: 'var(--color-text-primary)',
            }}
          >
            My Invitations
          </span>
          <FilterChips value={filter} onChange={setFilter} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-dark-elevated-1)' }}>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Email</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Member type</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Status</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Expires</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
                    Loading…
                  </td>
                </tr>
              ) : invitations.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
                    No invitations found
                  </td>
                </tr>
              ) : (
                invitations.map((inv) => {
                  const status = deriveStatus(inv);
                  return (
                    <tr key={inv.id} style={{ transition: 'background-color 80ms ease' }}>
                      {/* Email */}
                      <td style={{ ...cellStyle, color: 'var(--color-text-primary)', fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {inv.email}
                      </td>

                      {/* Role */}
                      <td style={cellStyle}>
                        <span style={{ textTransform: 'capitalize' }}>{inv.intended_role}</span>
                      </td>

                      {/* Status */}
                      <td style={cellStyle}>
                        <StatusBadge status={status} />
                      </td>

                      {/* Expires */}
                      <td style={cellStyle}>{formatDate(inv.expires_at)}</td>

                      {/* Created */}
                      <td style={cellStyle}>{formatDate(inv.created_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
